import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import ChatIcon from "@mui/icons-material/Chat";
import SpeakerNotesOffIcon from "@mui/icons-material/SpeakerNotesOff";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
  MeetingAttendee,
  CapabilityAxis,
  CapabilityValue,
} from "../../api/video_platform";

/** Axes the host can lock/unlock individually. */
type LockableAxis =
  | CapabilityAxis.Audio
  | CapabilityAxis.Video
  | CapabilityAxis.Content
  | CapabilityAxis.Chat;

const AXIS_LABEL: Record<LockableAxis, string> = {
  [CapabilityAxis.Audio]: "Audio",
  [CapabilityAxis.Video]: "Video",
  [CapabilityAxis.Content]: "Screen Share",
  [CapabilityAxis.Chat]: "Chat",
};

/** Axis → (unlocked icon, locked icon). Drives both the inline and menu views. */
const AXES: {
  axis: LockableAxis;
  Unlocked: React.ElementType;
  Locked: React.ElementType;
}[] = [
  { axis: CapabilityAxis.Audio, Unlocked: MicIcon, Locked: MicOffIcon },
  { axis: CapabilityAxis.Video, Unlocked: VideocamIcon, Locked: VideocamOffIcon },
  {
    axis: CapabilityAxis.Content,
    Unlocked: ScreenShareIcon,
    Locked: StopScreenShareIcon,
  },
  { axis: CapabilityAxis.Chat, Unlocked: ChatIcon, Locked: SpeakerNotesOffIcon },
];

/** Extra row action appended to the overflow menu on small screens. */
export type CapabilityExtraAction = {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
};

/** Pending confirmation: either a per-axis lock toggle, or a ban toggle. */
type PendingConfirm =
  | { kind: "axis"; axis: LockableAxis; willLock: boolean }
  | { kind: "ban"; willBan: boolean };

interface AttendeeCapabilityControlsProps {
  attendee: MeetingAttendee;
  meetingId: string;
  onUpdateCapability: (
    attendeeId: string,
    axis: LockableAxis,
    value: CapabilityValue,
    lock: boolean
  ) => Promise<void>;
  onToggleBan: (attendeeId: string, currentlyBanned: boolean) => Promise<void>;
  disabled?: boolean;
  /** Appended to the overflow menu on small screens (e.g. "Copy JSON"). */
  extraActions?: CapabilityExtraAction[];
}

const AttendeeCapabilityControls: React.FC<AttendeeCapabilityControlsProps> = ({
  attendee,
  meetingId: _meetingId,
  onUpdateCapability,
  onToggleBan,
  disabled = false,
  extraActions = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Identifies which row is currently in-flight — also doubles as the
  // disabled flag for that button while loading.
  const [loadingKey, setLoadingKey] = useState<LockableAxis | "ban" | null>(
    null
  );
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const isLocked = (axis: LockableAxis): boolean =>
    attendee.capabilities?.[axis]?.isLocked ?? false;

  const handleAxisClick = (axis: LockableAxis) =>
    setPending({ kind: "axis", axis, willLock: !isLocked(axis) });

  const handleBanClick = () =>
    setPending({ kind: "ban", willBan: !attendee.isBanned });

  const handleCancel = () => setPending(null);

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.kind === "axis") {
        setLoadingKey(pending.axis);
        const value = pending.willLock
          ? CapabilityValue.None
          : CapabilityValue.SendReceive;
        await onUpdateCapability(
          attendee.id,
          pending.axis,
          value,
          pending.willLock
        );
      } else {
        setLoadingKey("ban");
        await onToggleBan(attendee.id, attendee.isBanned);
      }
    } finally {
      setLoadingKey(null);
      setPending(null);
    }
  };

  /** Renders a single axis lock/unlock icon. */
  const renderAxisIcon = (
    axis: LockableAxis,
    UnlockedIcon: React.ElementType,
    LockedIcon: React.ElementType
  ) => {
    const locked = isLocked(axis);
    const label = AXIS_LABEL[axis];
    const tip = locked
      ? `${label} is locked — click to unlock`
      : `${label} is unlocked — click to lock`;
    return (
      <Tooltip title={tip}>
        <span>
          <IconButton
            size="small"
            onClick={() => handleAxisClick(axis)}
            disabled={disabled || loadingKey === axis}
            color={locked ? "error" : "default"}
          >
            {loadingKey === axis ? (
              <CircularProgress size={20} />
            ) : locked ? (
              <LockedIcon fontSize="small" />
            ) : (
              <UnlockedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderBanIcon = () => {
    const banned = attendee.isBanned;
    const tip = banned
      ? "User is banned — click to unban"
      : "Ban this user from the meeting";
    return (
      <Tooltip title={tip}>
        <span>
          <IconButton
            size="small"
            onClick={handleBanClick}
            disabled={disabled || loadingKey === "ban"}
            color={banned ? "error" : "default"}
          >
            {loadingKey === "ban" ? (
              <CircularProgress size={20} />
            ) : banned ? (
              <LockOpenIcon fontSize="small" />
            ) : (
              <BlockIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  /** Builds the title + body for the confirmation dialog. */
  const dialogCopy = (): { title: string; body: React.ReactNode } | null => {
    if (!pending) return null;
    const who = attendee.attendeeName || attendee.externalUserId;
    if (pending.kind === "axis") {
      const label = AXIS_LABEL[pending.axis];
      if (pending.willLock) {
        return {
          title: `Lock ${label}?`,
          body: (
            <>
              This will <strong>lock</strong> the <strong>{label}</strong>{" "}
              capability for <strong>{who}</strong>.
              <br />
              <br />
              The attendee will <strong>NOT</strong> be able to change their{" "}
              {label.toLowerCase()} settings. Only admins can control it.
            </>
          ),
        };
      }
      return {
        title: `Unlock ${label}?`,
        body: (
          <>
            This will <strong>unlock</strong> the <strong>{label}</strong>{" "}
            capability for <strong>{who}</strong>.
            <br />
            <br />
            The attendee will regain control of their{" "}
            {label.toLowerCase()}.
          </>
        ),
      };
    }
    // ban
    if (pending.willBan) {
      return {
        title: "Ban user from meeting?",
        body: (
          <>
            This will <strong>ban</strong> <strong>{who}</strong> from this
            meeting. They will be force-disconnected and won't be able to
            rejoin (the ban persists across sessions of this meeting).
            <br />
            <br />
            Other meetings are <strong>not</strong> affected.
          </>
        ),
      };
    }
    return {
      title: "Unban user?",
      body: (
        <>
          This will <strong>lift the ban</strong> on <strong>{who}</strong>{" "}
          for this meeting, allowing them to rejoin.
        </>
      ),
    };
  };

  const copy = dialogCopy();

  const confirmDialog = (
    <Dialog open={!!pending} onClose={handleCancel}>
      <DialogTitle>{copy?.title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{copy?.body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color={
            pending?.kind === "ban" && pending.willBan ? "error" : "primary"
          }
          variant="contained"
          autoFocus
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Mobile: collapse the axis toggles, ban, and any extra actions into a
  // single overflow menu so the row doesn't overflow the viewport.
  if (isMobile) {
    const banned = attendee.isBanned;
    const closeMenu = () => setMenuAnchor(null);
    return (
      <>
        <IconButton
          size="small"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={disabled}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
        >
          {AXES.map(({ axis, Unlocked, Locked }) => {
            const locked = isLocked(axis);
            const Icon = locked ? Locked : Unlocked;
            return (
              <MenuItem
                key={axis}
                disabled={disabled || loadingKey === axis}
                onClick={() => {
                  closeMenu();
                  handleAxisClick(axis);
                }}
              >
                <ListItemIcon sx={{ color: locked ? "error.main" : undefined }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {locked
                    ? `Unlock ${AXIS_LABEL[axis]}`
                    : `Lock ${AXIS_LABEL[axis]}`}
                </ListItemText>
              </MenuItem>
            );
          })}
          <MenuItem
            disabled={disabled || loadingKey === "ban"}
            onClick={() => {
              closeMenu();
              handleBanClick();
            }}
          >
            <ListItemIcon sx={{ color: banned ? "error.main" : undefined }}>
              {banned ? (
                <LockOpenIcon fontSize="small" />
              ) : (
                <BlockIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>{banned ? "Unban user" : "Ban user"}</ListItemText>
          </MenuItem>
          {extraActions.map((action) => (
            <MenuItem
              key={action.key}
              onClick={() => {
                closeMenu();
                action.onClick();
              }}
            >
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
        {confirmDialog}
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {renderAxisIcon(CapabilityAxis.Audio, MicIcon, MicOffIcon)}
      {renderAxisIcon(CapabilityAxis.Video, VideocamIcon, VideocamOffIcon)}
      {renderAxisIcon(
        CapabilityAxis.Content,
        ScreenShareIcon,
        StopScreenShareIcon
      )}
      {renderAxisIcon(CapabilityAxis.Chat, ChatIcon, SpeakerNotesOffIcon)}
      {renderBanIcon()}
      {confirmDialog}
    </Box>
  );
};

export default AttendeeCapabilityControls;
